import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';

import {
  Box,
  Button,
  Container,
  GridLegacy as Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { buildChannelMarkupIdentifiers } from '~/components/ChannelMarkup/channelMarkupIdentifier';
import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import { ROLE_CHANNEL_MARKUP_ADMIN_USER } from '~/consts/roles';

import useAppNavigation from '~/hooks/useAppNavigation';
import useCurrentTenant from '~/hooks/useCurrentTenant';
import useCurrentUser from '~/hooks/useCurrentUser';
import useQuery from '~/hooks/useQuery';

import { expireChannelMarkup, getChannelMarkupById, getChannelMarkupList } from '~/services/PromoService';
import { customerPortalHost } from '~/services/common';

import dateFormatter from '~/utils/dateFormatter';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: false,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 1,
};

const CHANNEL_MARKUP_ID_QUERY_PARAM = 'channel_markup_id';

const renderMarkupValue = (value: number | null | undefined) => {
  if (value == null || value === 0) {
    return '--';
  }

  return value;
};

const getColumns = ({
  handleExpireChannelMarkup,
  tenant,
}: {
  handleExpireChannelMarkup: ({ id }: { id: string }) => Promise<void>;
  tenant: App.Tenant;
}): Array<GridColDef> => [
  {
    field: 'channelMarkupIdentifier',
    headerName: 'Identifier',
    renderCell: (params) => {
      const identifiers = buildChannelMarkupIdentifiers(params.row.rules?.markups ?? []);

      if (identifiers.length === 0) {
        return <Typography>-</Typography>;
      }

      return (
        <Stack spacing={0.25}>
          {identifiers.map((identifier) => (
            <Typography key={identifier} sx={{ fontWeight: 600 }}>
              {identifier}
            </Typography>
          ))}
        </Stack>
      );
    },
    ...sharedOpts,
  },
  {
    field: 'channel_markup_id',
    headerName: 'ID',
    ...sharedOpts,
  },
  {
    field: 'version',
    headerName: 'Version',
    renderCell: (params) => {
      const version = params.row.version ?? 'v1';

      return <Typography sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{version}</Typography>;
    },
    ...sharedOpts,
  },
  {
    field: 'utm',
    headerName: 'UTM Params',
    ...sharedOpts,
    renderCell: (params) => {
      return (
        <Stack>
          <Typography>
            <b>UTM source</b> {params.row.source}
          </Typography>
          <Typography>
            <b>UTM medium</b> {params.row.medium}
          </Typography>
          <Typography>
            <b>UTM campaign</b> {params.row.campaign}
          </Typography>
          {params.row.term && (
            <Typography>
              <b>UTM term</b> {params.row.term}
            </Typography>
          )}
        </Stack>
      );
    },
  },
  {
    field: 'link',
    headerName: 'Link',
    ...sharedOpts,
    renderCell: (params) => {
      const query = {
        utm_source: params.row.source,
        utm_medium: params.row.medium,
        utm_campaign: params.row.campaign,
        ...(params.row.term ? { utm_term: params.row.term } : {}),
      };
      const queryString = qs.stringify(query);
      return (
        <Stack>
          <Link href={`${customerPortalHost(tenant)}?${queryString}`}>Customer Portal Link</Link>
        </Stack>
      );
    },
  },
  {
    field: 'markup',
    headerName: 'Normal Price Markup',
    renderCell: (params) => <Typography sx={{ fontWeight: 600 }}>{renderMarkupValue(params.row.markup)}</Typography>,
    ...sharedOpts,
  },
  {
    field: 'luxplus_markup',
    headerName: 'Lux Plus Markup',
    renderCell: (params) => (
      <Typography sx={{ fontWeight: 600 }}>{renderMarkupValue(params.row.luxplus_markup)}</Typography>
    ),
    ...sharedOpts,
  },
  {
    field: 'created_at',
    headerName: 'Created At',
    renderCell: (params) => dateFormatter(params.row.created_at),
    ...sharedOpts,
  },
  {
    field: 'expires_at',
    headerName: 'Expires At',
    renderCell: (params) => dateFormatter(params.row.expires_at),
    ...sharedOpts,
  },
  {
    field: 'operation',
    headerName: 'Operation',
    renderCell: (params) => {
      const isExpired = params.row.expires_at && new Date(params.row.expires_at) < new Date();
      return (
        <Button
          variant="contained"
          color="secondary"
          disabled={isExpired}
          onClick={() => {
            handleExpireChannelMarkup({ id: params.row.channel_markup_id });
          }}
        >
          Expire
        </Button>
      );
    },
    ...sharedOpts,
  },
];

type ChannelMarkupStatus = 'active' | 'expired' | 'all';

function ChannelMarkupContainer() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageNum, setPageNum] = useState<number>(query.get('page_num') ? Number(query.get('page_num')) : 0);
  const [pageSize, setPageSize] = useState<number>(query.get('page_size') ? Number(query.get('page_size')) : 10);
  const [totalCount, setTotalCount] = useState(0);
  const { tenant } = useCurrentTenant();
  const [status, setStatus] = useState<ChannelMarkupStatus>((query.get('status') as ChannelMarkupStatus) || 'all');
  const selectedChannelMarkupId = query.get(CHANNEL_MARKUP_ID_QUERY_PARAM);
  const { push } = useAppNavigation();

  const { user } = useCurrentUser();

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum.toString(),
      page_size: pageSize.toString(),
      status: status,
      ...(selectedChannelMarkupId ? { [CHANNEL_MARKUP_ID_QUERY_PARAM]: selectedChannelMarkupId } : {}),
    });
    push({
      search: `?${searchParams}`,
    });
  }, [push, pageNum, pageSize, selectedChannelMarkupId, status]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (selectedChannelMarkupId) {
        const { result: channelMarkup } = await getChannelMarkupById(selectedChannelMarkupId);

        if (channelMarkup.version !== 'v1') {
          throw new Error('The selected channel markup is not a v1 markup');
        }

        setData([channelMarkup]);
        setTotalCount(1);
        setQueryParams();
        return;
      }

      const {
        result: { channelMarkupList, total },
      } = await getChannelMarkupList({
        pageNum: pageNum,
        pageSize: pageSize,
        brand: tenant.brand,
        status: status,
      });
      setData(channelMarkupList ?? []);
      setTotalCount(Number(total));
      setQueryParams();
    } catch {
      enqueueSnackbar('Channel Markup fetch failed', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [pageNum, pageSize, tenant.brand, status, selectedChannelMarkupId, setQueryParams, enqueueSnackbar]);

  const dataRows = data.map((row, index) => ({
    id: row.channel_markup_id ?? index,
    ...row,
  }));

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
      query.set('page_num', page.toString());
      query.set('page_size', pageSize.toString());
      window.history.pushState({}, '', `${window.location.pathname}?${query.toString()}`);
    },
    [query],
  );

  const handleExpireChannelMarkup = useCallback(
    async ({ id }: { id: string }) => {
      if (!user?.roles?.includes(ROLE_CHANNEL_MARKUP_ADMIN_USER)) {
        enqueueSnackbar('You do not have permission to expire channel markup', { variant: 'warning' });
        return;
      }

      try {
        await expireChannelMarkup(id);
        enqueueSnackbar('Expired the markup successfully', { variant: 'success' });
        fetchData();
      } catch (error) {
        enqueueSnackbar(`Failed to expired the markup: ${error}`, { variant: 'error' });
      }
    },
    [enqueueSnackbar, fetchData, user?.roles],
  );

  // START - Status Filter
  const handleStatusChange = (event) => {
    setStatus(event.target.value);
    fetchData();
  };
  // END - Status Filter

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Channel Markup</title>
      </Helmet>

      <PageSubheader title={`Search Channel Markup [${data?.length} of ${totalCount}]`} />

      <Grid container spacing={2} justifyContent="flex-end" alignItems="end">
        <Grid item>
          <InputLabel>Status</InputLabel>
          <Select value={status} label="Status" onChange={handleStatusChange}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="expired">Expired</MenuItem>
          </Select>
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" component={Link} href="/marketing/channel-markup/create">
            New v1 Markup
          </Button>
        </Grid>
        <Grid item>
          <Button variant="text" component={Link} href="/marketing/channel-markup-v2">
            View Markup V2
          </Button>
        </Grid>
      </Grid>
      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={dataRows}
          columns={getColumns({
            handleExpireChannelMarkup,
            tenant: tenant,
          })}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[10, 20, 50]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
          rowHeight={100}
          getRowClassName={(params) =>
            params.row.channel_markup_id === selectedChannelMarkupId ? 'channel-markup-selected-row' : ''
          }
          sx={{
            '& .channel-markup-selected-row': {
              bgcolor: 'action.selected',
            },
            '& .channel-markup-selected-row:hover': {
              bgcolor: 'action.selected',
            },
          }}
        />
      </Box>
    </Container>
  );
}

export default ChannelMarkupContainer;
